/**
 * PhotoGallery — displays the user's uploaded photos as a grid.
 *
 * Fetches GET /photos on mount. Each photo card shows the image (via presigned viewUrl).
 * Click → modal with session start options.
 *
 * HEIC note: browser preview may not work for HEIC files (Phase 3 will add conversion).
 * Empty state: "No photos yet" message.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { PhotoListItem } from '../../lib/photoUpload';

const REST_API_URL = import.meta.env.VITE_REST_API_URL ?? '';

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No id_token');
  return token;
}

interface PhotoActionMenuProps {
  photo: PhotoListItem;
  onClose: () => void;
}

function PhotoActionMenu({ photo, onClose }: PhotoActionMenuProps) {
  const navigate = useNavigate();

  const handleStartSession = (mode: 'voice' | 'text') => {
    onClose();
    navigate(`/session/new?photoId=${photo.photoId}&mode=${mode}`);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo actions"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Photo preview */}
        {photo.viewUrl && (
          <img
            src={photo.viewUrl}
            alt="Selected photo"
            className="w-full h-48 object-cover rounded-xl bg-stone-100"
          />
        )}

        <p className="text-xs text-stone-500">
          Uploaded {new Date(photo.uploadedAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => handleStartSession('voice')}
            className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors"
          >
            Start Voice Session about this photo
          </button>
          <button
            type="button"
            onClick={() => handleStartSession('text')}
            className="w-full py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold text-sm hover:bg-stone-50 transition-colors"
          >
            Start Text Session about this photo
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-sm text-stone-500 hover:text-stone-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function PhotoGallery() {
  const [photos, setPhotos] = useState<PhotoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoListItem | null>(null);

  const fetchPhotos = useCallback(async () => {
    try {
      setError(null);
      const token = await getIdToken();
      const response = await fetch(`${REST_API_URL}/photos`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load photos (${response.status})`);
      }

      const data = await response.json() as { photos: PhotoListItem[] };
      setPhotos(data.photos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPhotos();
  }, [fetchPhotos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-stone-400 text-sm">Loading photos…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-700 bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
        {error}
        <button
          type="button"
          onClick={() => { setLoading(true); void fetchPhotos(); }}
          className="ml-2 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 text-stone-500 text-sm">
        No photos yet. Upload one above to start a memory session around it.
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {photos.map((photo) => (
          <button
            key={photo.photoId}
            type="button"
            onClick={() => setSelectedPhoto(photo)}
            className="aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200 hover:border-amber-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1"
            aria-label={`Photo uploaded ${new Date(photo.uploadedAt).toLocaleDateString()}`}
          >
            {photo.viewUrl && photo.status === 'ready' ? (
              <img
                src={photo.viewUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              // Fallback for HEIC (may not preview in all browsers) or pending/error
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-stone-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                  />
                </svg>
                <span className="text-xs text-stone-400 text-center leading-tight">
                  {photo.status === 'pending' ? 'Processing…' : photo.contentType.split('/')[1]?.toUpperCase() ?? 'Photo'}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Photo action modal */}
      {selectedPhoto && (
        <PhotoActionMenu
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </>
  );
}
